import { useEffect } from 'react';
import { toast } from 'sonner';

/**
 * Keyboard shortcut handler for the 3D editor.
 */
export function useKeyboardShortcuts({
  undo, redo, duplicateSelected, duplicateMultiSelected,
  deleteSelected, selectedObjectId, toggleGrid, deselectAll,
  editMode, multiSelectIds, projectors, removeProjector,
  removeSceneObject, setMultiSelectIds, handleSave,
  setViewPreset, showAllBeams, hideAllBeams, setMeasurementMode,
  measurementMode, updateProjector, createCamera, getCameraStateRef,
}) {
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMeta = e.metaKey || e.ctrlKey;
      const isInput = e.target.matches('input, textarea');

      // ── File shortcuts ──
      if (isMeta && e.key === 's') { e.preventDefault(); handleSave(); return; }
      if (isMeta && e.key === 'z' && !e.shiftKey) { if (editMode) return; e.preventDefault(); undo(); return; }
      if ((isMeta && e.key === 'z' && e.shiftKey) || (isMeta && e.key === 'y')) { if (editMode) return; e.preventDefault(); redo(); return; }

      // ── Duplicate ──
      if (isMeta && e.key === 'd') {
        e.preventDefault();
        if (multiSelectIds.length > 0) duplicateMultiSelected(multiSelectIds);
        else duplicateSelected();
        return;
      }

      // ── Delete ──
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (!isInput && !editMode) {
          if (multiSelectIds.length > 0) {
            e.preventDefault();
            multiSelectIds.forEach(id => {
              if (projectors.find(p => p.id === id)) removeProjector(id);
              else removeSceneObject(id);
            });
            toast.success(`Deleted ${multiSelectIds.length} items`);
            setMultiSelectIds([]);
          } else if (selectedObjectId) {
            e.preventDefault(); deleteSelected();
          }
        }
        return;
      }

      // Skip remaining shortcuts if typing in inputs
      if (isInput) return;

      // ── Escape — deselect ──
      if (e.key === 'Escape') { deselectAll(); return; }

      // ── Grid toggle ──
      if (e.key === 'g' && !isMeta && !e.shiftKey) { toggleGrid(); return; }

      // ── Measurement mode ──
      if (e.key === 'm' && !isMeta) {
        if (setMeasurementMode) {
          setMeasurementMode(prev => {
            if (!prev) toast.info('Click first point to start measuring');
            return !prev;
          });
        }
        return;
      }

      // ── Projector beams ──
      if (e.key === 'b' && !isMeta && !e.shiftKey) { if (showAllBeams) showAllBeams(); return; }
      if (e.key === 'B' && !isMeta && e.shiftKey) { if (hideAllBeams) hideAllBeams(); return; }

      // ── Projectors on/off ──
      if (e.key === 'P' && e.shiftKey && !isMeta) {
        if (updateProjector && projectors?.length > 0) {
          const allOn = projectors.every(p => p.power_on !== false);
          projectors.forEach(p => updateProjector(p.id, { power_on: !allOn }));
          toast.success(allOn ? 'All projectors OFF' : 'All projectors ON');
        }
        return;
      }

      // ── Projector borders on/off ──
      if (e.key === 'O' && e.shiftKey && !isMeta) {
        if (updateProjector && projectors?.length > 0) {
          const allBorders = projectors.every(p => p.show_border !== false);
          projectors.forEach(p => updateProjector(p.id, { show_border: !allBorders }));
          toast.success(allBorders ? 'All borders OFF' : 'All borders ON');
        }
        return;
      }

      // ── Add camera ──
      if (isMeta && e.shiftKey && (e.key === 'c' || e.key === 'C')) {
        e.preventDefault();
        if (getCameraStateRef?.current && createCamera) {
          const state = getCameraStateRef.current();
          if (state) { createCamera(state); toast.success('Camera added'); }
        }
        return;
      }

      // ── Viewport presets (Numpad style: 1=Front, 3=Right, 7=Top, 0=Reset) ──
      if (setViewPreset) {
        if (e.key === '0' && !isMeta) { setViewPreset('reset'); return; }
        if (e.key === '1' && !isMeta) { setViewPreset('front'); return; }
        if (e.key === '1' && isMeta) { e.preventDefault(); setViewPreset('back'); return; }
        if (e.key === '3' && !isMeta) { setViewPreset('right'); return; }
        if (e.key === '3' && isMeta) { e.preventDefault(); setViewPreset('left'); return; }
        if (e.key === '7' && !isMeta) { setViewPreset('top'); return; }
        if (e.key === '7' && isMeta) { e.preventDefault(); setViewPreset('bottom'); return; }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    undo, redo, duplicateSelected, duplicateMultiSelected,
    deleteSelected, selectedObjectId, toggleGrid, deselectAll,
    editMode, multiSelectIds, projectors, removeProjector,
    removeSceneObject, setMultiSelectIds, handleSave,
    setViewPreset, showAllBeams, hideAllBeams, setMeasurementMode,
    measurementMode, updateProjector, createCamera, getCameraStateRef,
  ]);
}
